import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Badge } from '@ooh/ui/components/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';
import { computeCountdown } from '../lib/countdown';
import { formatDisplayDate } from '../../../lib/date-format';
import type { MediaUnitTrashItem } from '@ooh/contracts';

// ─── CountdownCell ────────────────────────────────────────────

function CountdownCell({ expiresAt }: { expiresAt: string }) {
  const { t } = useTranslation('inventory');
  const result = computeCountdown(expiresAt);
  if (result.type === 'expired') {
    return <span className="text-destructive">{t('trash.countdown.expired')}</span>;
  }
  if (result.type === 'today') {
    return <span className="text-warning">{t('trash.countdown.today')}</span>;
  }
  return <span>{t('trash.countdown.remaining', { days: result.days, hours: result.hours })}</span>;
}

// ─── ReferencedByOffersBadge ──────────────────────────────────

function ReferencedByOffersBadge({ count }: { count: number }) {
  const { t } = useTranslation('inventory');
  if (!(count > 0)) {
    return null;
  }
  return (
    <Badge variant="warning" className="mt-1 whitespace-normal text-left font-normal">
      {t('trash.referencedByOffers', { count })}
    </Badge>
  );
}

// ─── TrashTable ───────────────────────────────────────────────

export interface TrashTableProps {
  items: MediaUnitTrashItem[];
  isRestorePending: boolean;
  onRestore: (id: number) => void;
}

export function TrashTable({ items, isRestorePending, onRestore }: TrashTableProps) {
  const { t } = useTranslation('inventory');

  if (items.length === 0) {
    return (
      <div className="h-full p-6">
        <div
          className="flex h-full flex-col items-center justify-center gap-4 text-center"
          data-testid="trash-empty-state"
        >
          <Trash2 className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">{t('trash.emptyState')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('trash.table.columns.grwId')}</TableHead>
              <TableHead>{t('trash.table.columns.city')}</TableHead>
              <TableHead>{t('trash.table.columns.deletedAt')}</TableHead>
              <TableHead>{t('trash.table.columns.expiresAt')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-mono text-xs">{item.grwId}</TableCell>
                <TableCell>{item.city}</TableCell>
                <TableCell>{formatDisplayDate(item.deletedAt)}</TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <CountdownCell expiresAt={item.expiresAt} />
                    <ReferencedByOffersBadge count={item.offerReferenceCount} />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isRestorePending}
                    onClick={() => onRestore(item.id)}
                  >
                    {t('trash.actions.restore')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
